import React from 'react';

import AlloyEmptyState, { EmptyStateSize } from '@adsk/alloy-react-empty-state';
import { IllustrationType } from '@adsk/alloy-react-illustration';
import { MarginMixinTypes, StylableComponent } from '@adsk/nirvana-theme';

import { Wrapper, Action, Footer } from './styles';

type EmptyStateProps = Omit<StylableComponent<HTMLDivElement>, 'title'> &
  MarginMixinTypes & {
    /** Illustration icon name supported by EmptyState */
    illustration?: IllustrationType;
    /** Illustration gray mode */
    gray?: boolean;
    /** Whether to display at the container center. */
    centered?: boolean;
    /** Title of the empty state */
    title?: React.ReactNode;
    /** Detail footer of the empty state */
    footer?: React.ReactNode;
    /** Detail description of the empty state */
    description?: React.ReactNode;
    /** Whether hide the default illustration */
    hideIllustration?: boolean;
    /** The empty state size */
    size?: EmptyStateSize;
    /** The child component */
    children?: React.ReactNode;
  };

/**
 * EmptyState
 *
 * Composed Alloy component `@adsk/alloy-react-empty-state` with extra props:
 *  1. `footer` to add an extra footer
 *  2. `centered` to center the empty state
 */
const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ illustration, gray = true, centered = false, children, footer, ...rest }, ref) => {
    const illustrationType =
      illustration && gray && !illustration.endsWith('Grey') ? `${illustration}Grey` : illustration;

    return (
      <Wrapper
        data-testid="empty-state"
        ref={ref}
        illustrationType={illustrationType as IllustrationType}
        {...rest}
        centered={centered}
      >
        {children && <Action data-testid="empty-state-action">{children}</Action>}
        {footer && <Footer data-testid="empty-state-footer">{footer}</Footer>}
      </Wrapper>
    );
  }
);

EmptyState.displayName = 'EmptyState';

export type { IllustrationType, EmptyStateSize };
export default Object.assign(EmptyState, {
  Illustrations: AlloyEmptyState.ILLUSTRATION_TYPES,
  Sizes: AlloyEmptyState.SIZES
});
